// Feedback panel for resolution results — shown when awaiting user feedback
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { CheckCircle, RotateCcw, MessageSquare } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

interface ResolutionFeedbackPanelProps {
  issueId: string
  organizationId: string
  isResolved?: boolean
  onResolved: () => void
  onRetry: () => void
  onContinueChat: () => void
}

export function ResolutionFeedbackPanel({
  issueId, organizationId, isResolved = false, onResolved, onRetry, onContinueChat,
}: ResolutionFeedbackPanelProps): JSX.Element {
  const { user } = useAuth()
  const [feedback, setFeedback] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleResolved = async () => {
    setIsSubmitting(true)
    try {
      await supabase.from('issues').update({
        status: 'resolved',
        resolved_at: new Date().toISOString(),
        resolved_by: user?.id,
        resolution_summary: feedback || 'Resolution confirmed by user.',
      } as never).eq('id', issueId)
      onResolved()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFailed = async () => {
    setIsSubmitting(true)
    try {
      // Save feedback as system comment
      if (feedback.trim() && user) {
        await supabase.from('issue_conversations').insert({
          issue_id: issueId, organization_id: organizationId,
          user_id: user.id, role: 'system',
          content: `Resolution feedback: ${feedback.trim()}`,
          is_internal: false, metadata: { type: 'resolution_feedback' },
        } as never)
      }

      // Reset pipeline for re-run: clear cache, set status back
      const { data } = await supabase.from('issues')
        .select('ai_analysis').eq('id', issueId).single()
      const existing = (data?.ai_analysis as Record<string, unknown>) || {}

      await supabase.from('issues').update({
        status: 'in_progress',
        resolved_at: null,
        resolved_by: null,
        resolution_summary: null,
        ai_analysis: {
          ...existing,
          resolutionPipelineStatus: 'pending',
          cachedInstructions: {},
          _acceptedTier: null,
          resolvedByTier: null,
          previousFeedback: feedback.trim() || undefined,
        },
      } as never).eq('id', issueId)

      onRetry()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Card className="border-kaycoo-orange bg-orange-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-kaycoo-teal-dark">
          {isResolved ? 'Resolution Applied — Did it work?' : 'Resolution Sent to User — Awaiting Feedback'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What happened when the user tried the resolution?"
          className="w-full rounded-md border border-kaycoo-white-3 p-2 text-sm"
          rows={3}
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={handleResolved} disabled={isSubmitting}
            className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700">
            <CheckCircle className="h-3.5 w-3.5" /> Resolution Worked
          </Button>
          <Button size="sm" variant="outline" onClick={handleFailed} disabled={isSubmitting}
            className="gap-1.5 border-kaycoo-burgundy text-kaycoo-burgundy hover:bg-kaycoo-burgundy-pale">
            <RotateCcw className="h-3.5 w-3.5" /> Resolution Failed — Re-run
          </Button>
          <Button size="sm" variant="ghost" onClick={onContinueChat} disabled={isSubmitting}
            className="gap-1.5 text-kaycoo-teal-dark hover:bg-kaycoo-teal-pale">
            <MessageSquare className="h-3.5 w-3.5" /> Add More Info
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
